import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { formatApiError } from "../lib/auth";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { StatusBadge } from "../components/StatusBadge";
import { toast } from "sonner";
import { Ban, Eye, AlertCircle } from "lucide-react";

export default function Takedown() {
  const [records, setRecords] = useState([]);
  const [details, setDetails] = useState(null);

  const load = () => api.get("/takedown").then((r) => setRecords(r.data));
  useEffect(() => { load(); }, []);

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-12">
      <div className="text-xs font-semibold tracking-[0.2em] uppercase text-cyan-400">Rights Management</div>
      <h1 className="mt-2 text-4xl font-bold tracking-tight flex items-center gap-3">
        <Ban className="w-8 h-8 text-rose-400" /> Takedown Requests
      </h1>
      <p className="mt-2 text-zinc-500 text-sm">All takedown requests you have submitted. New requests are created from a song's details page.</p>

      <div className="mt-8 rounded-2xl border border-zinc-800 bg-zinc-950/60 overflow-x-auto">
        <table className="w-full min-w-[900px]" data-testid="td-user-table">
          <thead>
            <tr className="text-left text-xs uppercase tracking-wider text-zinc-500 border-b border-zinc-800">
              <th className="p-4">Request ID</th>
              <th className="p-4">Song</th>
              <th className="p-4">UPC / ISRC</th>
              <th className="p-4">Request Date</th>
              <th className="p-4">Reason</th>
              <th className="p-4">Status</th>
              <th className="p-4">Last Updated</th>
              <th className="p-4 text-right"></th>
            </tr>
          </thead>
          <tbody>
            {records.map((r) => (
              <tr key={r.id} data-testid={`td-row-${r.id}`} className="border-b border-zinc-900 hover:bg-zinc-900/40">
                <td className="p-4 font-mono text-cyan-400 text-sm">{r.id}</td>
                <td className="p-4">
                  <div className="font-medium">{r.song_title}</div>
                  <div className="text-xs text-zinc-500">{r.primary_artist_name}</div>
                </td>
                <td className="p-4 font-mono text-xs text-emerald-400">{r.upc || "—"}<br />{r.isrc || "—"}</td>
                <td className="p-4 text-zinc-400 text-sm">{new Date(r.created_at).toLocaleDateString()}</td>
                <td className="p-4 text-sm">{r.reason === "Other" ? r.reason_other : r.reason}</td>
                <td className="p-4"><StatusBadge status={r.status} /></td>
                <td className="p-4 text-zinc-500 text-xs">{new Date(r.updated_at).toLocaleString()}</td>
                <td className="p-4 text-right">
                  <Button size="sm" variant="ghost" onClick={() => setDetails(r)} data-testid={`td-view-${r.id}`}
                    className="text-cyan-400 h-7"><Eye className="w-3.5 h-3.5 mr-1" /> View</Button>
                </td>
              </tr>
            ))}
            {records.length === 0 && <tr><td colSpan="8" className="p-12 text-center text-zinc-500">No takedown requests yet.</td></tr>}
          </tbody>
        </table>
      </div>

      <Dialog open={!!details} onOpenChange={(o) => !o && setDetails(null)}>
        <DialogContent className="bg-zinc-950 border-zinc-800 max-w-lg" data-testid="td-details-dialog">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Ban className="w-5 h-5 text-rose-400" /> Takedown Request</DialogTitle></DialogHeader>
          {details && (
            <div className="space-y-3 text-sm">
              <D k="Request ID" v={<code className="text-cyan-400">{details.id}</code>} />
              <D k="Song" v={details.song_title} />
              <D k="Primary Artist" v={details.primary_artist_name} />
              <D k="Submission ID" v={<code className="text-zinc-400 text-xs">{details.submission_id}</code>} />
              <D k="ISRC" v={<code className="text-emerald-400">{details.isrc || "—"}</code>} />
              <D k="UPC" v={<code className="text-emerald-400">{details.upc || "—"}</code>} />
              <D k="Reason" v={details.reason === "Other" ? details.reason_other : details.reason} />
              <D k="Status" v={<StatusBadge status={details.status} />} />
              <D k="Requested" v={new Date(details.created_at).toLocaleString()} />
              <D k="Last Updated" v={new Date(details.updated_at).toLocaleString()} />
              {details.completed_at && <D k="Completed" v={new Date(details.completed_at).toLocaleString()} />}
              {details.notes && (
                <div>
                  <div className="text-xs uppercase tracking-wider text-zinc-500 mb-1">Your Note</div>
                  <div className="p-3 rounded bg-zinc-900 border border-zinc-800 text-zinc-300">{details.notes}</div>
                </div>
              )}
              {details.admin_notes && (
                <div>
                  <div className="text-xs uppercase tracking-wider text-cyan-400 mb-1">Admin Reply</div>
                  <div className="p-3 rounded bg-cyan-500/5 border border-cyan-500/30 text-zinc-200">{details.admin_notes}</div>
                </div>
              )}
              {details.status_history?.length > 0 && (
                <div>
                  <div className="text-xs uppercase tracking-wider text-zinc-500 mb-1">Status Timeline</div>
                  <div className="space-y-1">
                    {details.status_history.map((h, i) => (
                      <div key={i} className="p-2 rounded bg-zinc-900 border border-zinc-800 text-xs">
                        <div className="flex justify-between text-zinc-400">
                          <span>{h.from ? `${h.from} → ` : ""}<span className="text-cyan-400">{h.to}</span></span>
                          <span>{new Date(h.at).toLocaleString()}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function D({ k, v }) {
  return (
    <div className="flex justify-between gap-4">
      <span className="text-zinc-500 uppercase tracking-wider text-xs">{k}</span>
      <span className="text-zinc-200 text-right">{v || "—"}</span>
    </div>
  );
}

export function TakedownFormDialog({ open, onOpenChange, song, onDone }) {
  const [reason, setReason] = useState("");
  const [reasonOther, setReasonOther] = useState("");
  const [notes, setNotes] = useState("");
  const [confirm, setConfirm] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) { setReason(""); setReasonOther(""); setNotes(""); setConfirm(false); }
  }, [open]);

  if (!song) return null;

  const submit = async () => {
    if (!reason) { toast.error("Please choose a reason"); return; }
    if (reason === "Other" && !reasonOther.trim()) { toast.error("Please describe the reason"); return; }
    if (!confirm) { toast.error("Please tick the confirmation box"); return; }
    setLoading(true);
    try {
      const { data } = await api.post("/takedown", {
        submission_id: song.id, reason,
        reason_other: reason === "Other" ? reasonOther : "",
        notes, confirm,
      });
      toast.success(`Takedown request submitted (${data.id})`);
      onOpenChange(false);
      onDone?.(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to submit");
    } finally { setLoading(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-zinc-950 border-zinc-800 max-w-lg max-h-[85vh] overflow-y-auto" data-testid="td-form-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><Ban className="w-5 h-5 text-rose-400" /> Request Takedown</DialogTitle></DialogHeader>
        <div className="space-y-3 text-sm">
          <div className="p-3 rounded bg-zinc-900 border border-zinc-800 space-y-1">
            <div className="flex justify-between"><span className="text-zinc-500">Song Title</span><span>{song.song_title}</span></div>
            <div className="flex justify-between"><span className="text-zinc-500">Primary Artist</span><span>{song.primary_artist_name}</span></div>
            <div className="flex justify-between"><span className="text-zinc-500">UPC</span><code className="text-emerald-400">{song.upc || "—"}</code></div>
            <div className="flex justify-between"><span className="text-zinc-500">ISRC</span><code className="text-emerald-400">{song.isrc || "—"}</code></div>
            <div className="flex justify-between"><span className="text-zinc-500">Submission ID</span><code className="text-cyan-400 text-xs">{song.id}</code></div>
            <div className="flex justify-between"><span className="text-zinc-500">Release Date</span><span>{song.release_date || "—"}</span></div>
          </div>

          <div>
            <Label className="text-xs uppercase tracking-wider text-zinc-400">Takedown Reason</Label>
            <div className="mt-2 grid grid-cols-2 gap-1.5">
              {["Artist Request","Wrong Metadata","Wrong Audio","Rights Issue","Duplicate Release","Label Request","Other"].map((r) => (
                <button key={r} type="button" onClick={() => setReason(r)}
                  data-testid={`td-reason-${r.replace(/\s+/g, '-').toLowerCase()}`}
                  className={`p-2 rounded-md text-xs text-left border transition-colors ${reason === r ? "border-cyan-400 bg-cyan-500/10 text-cyan-300" : "border-zinc-800 bg-zinc-900 text-zinc-400 hover:border-zinc-700"}`}>
                  {r}
                </button>
              ))}
            </div>
          </div>

          {reason === "Other" && (
            <div>
              <Label className="text-xs uppercase tracking-wider text-zinc-400">Please describe</Label>
              <Input value={reasonOther} onChange={(e) => setReasonOther(e.target.value)}
                data-testid="td-reason-other" className="mt-1.5 bg-zinc-900 border-zinc-800" />
            </div>
          )}

          <div>
            <Label className="text-xs uppercase tracking-wider text-zinc-400">Additional Notes</Label>
            <Textarea rows={3} value={notes} onChange={(e) => setNotes(e.target.value)}
              data-testid="td-notes" className="mt-1.5 bg-zinc-900 border-zinc-800" placeholder="Optional context for the admin..." />
          </div>

          <label className="flex items-start gap-2 p-3 rounded-lg bg-rose-500/5 border border-rose-500/30 cursor-pointer">
            <input type="checkbox" checked={confirm} onChange={(e) => setConfirm(e.target.checked)}
              data-testid="td-confirm" className="mt-1 accent-rose-400" />
            <span className="text-xs text-rose-200">
              <AlertCircle className="w-3.5 h-3.5 inline mr-1" />
              I confirm that I want to request takedown for this song. This does not delete the record — the admin will process the takedown.
            </span>
          </label>
        </div>
        <div className="flex justify-end gap-2 mt-4">
          <Button variant="outline" onClick={() => onOpenChange(false)} className="border-zinc-700 hover:bg-zinc-900">Cancel</Button>
          <Button onClick={submit} disabled={loading} data-testid="td-submit"
            className="bg-rose-500 hover:bg-rose-600 text-white font-semibold">
            {loading ? "Submitting..." : "Submit Takedown Request"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
